/**
 * semrushKeywords.ts — a keyword lookup in Semrush for the ad team: related and question keywords for one starting phrase,
 * in the US and Canada, with volume, cost per click, competition and intent. Pure: building the answer from Semrush's tables.
 *
 * Cost (Semrush's published price per line): related 40, questions 40, volume check 10. A lookup asks for `limit` lines
 * per list, so it can spend at most `estimateLookupUnits(limit)`; the run limit (6,000) always applies on top.
 * Column names of the intent and difficulty columns are not documented by Semrush, so they are matched loosely and a
 * figure that cannot be read is "not available", never 0.
 */
import { z } from "zod";
import { adProductIdSchema } from "./adsProducts.ts";
import { SEMRUSH_RUN_UNIT_CAP, type SemrushTable } from "./semrush.ts";

export const KEYWORD_MARKETS = ["us", "ca"] as const;
export type KeywordMarket = (typeof KEYWORD_MARKETS)[number];
export const KEYWORD_MARKET_LABEL: Record<KeywordMarket, string> = { us: "US", ca: "Canada" };

/** Semrush's intent codes, as listed in the team's requirements. */
export const KEYWORD_INTENT_LABEL: Record<number, string> = { 0: "Commercial", 1: "Informational", 2: "Navigational", 3: "Transactional" };

export const KEYWORD_LIMITS = { default: 10, max: 30, phraseMax: 120, unitsRelated: 40, unitsQuestions: 40, unitsOverview: 10, overviewBatch: 100 } as const;

/** The most a lookup of `limit` lines per list can spend: both lists in both markets, plus a volume check for the keywords one market is missing. */
export function estimateLookupUnits(limit: number): number {
  const lists = 2 * KEYWORD_MARKETS.length * limit * KEYWORD_LIMITS.unitsRelated;
  const checks = 2 * KEYWORD_MARKETS.length * limit * KEYWORD_LIMITS.unitsOverview;
  return Math.min(lists + checks, SEMRUSH_RUN_UNIT_CAP);
}

export const keywordLookupBodySchema = z.object({
  siteId: z.string().min(1),
  product: adProductIdSchema,
  phrase: z.string().trim().min(2, "Type a keyword first.").max(KEYWORD_LIMITS.phraseMax).refine((s) => !/[;\n\r]/.test(s), "One keyword or phrase at a time, without semicolons."),
  limit: z.number().int().min(1).max(KEYWORD_LIMITS.max).default(KEYWORD_LIMITS.default),
});
export type KeywordLookupBody = z.infer<typeof keywordLookupBodySchema>;

export const keywordMetricsSchema = z.object({
  volume: z.number().nullable(),
  cpc: z.number().nullable(),
  competition: z.number().nullable(),
  results: z.number().nullable(),
  difficulty: z.number().nullable(),
  /** Semrush intent codes (0–3); null when Semrush gave none we could read. */
  intent: z.array(z.number().int()).nullable(),
});
export type KeywordMetrics = z.infer<typeof keywordMetricsSchema>;

export const keywordRowSchema = z.object({
  keyword: z.string(),
  /** Which list(s) it came from. */
  from: z.array(z.enum(["related", "question"])).min(1),
  us: keywordMetricsSchema.nullable(),
  ca: keywordMetricsSchema.nullable(),
});
export type KeywordRow = z.infer<typeof keywordRowSchema>;

export const keywordLookupResultSchema = z.object({
  phrase: z.string(),
  rows: z.array(keywordRowSchema),
  unitsUsed: z.number(),
  /** True when the run limit stopped it before every list was fetched. */
  stoppedEarly: z.boolean(),
  /** Anything a person should know (a list that failed, a limit reached), in plain words. */
  notes: z.array(z.string()),
});
export type KeywordLookupResult = z.infer<typeof keywordLookupResultSchema>;

const norm = (s: string) => s.toLowerCase().replace(/[^a-z0-9]+/g, "");

/** A cell by column name: the first column whose normalised name equals or contains one of `names`. */
function cell(row: Record<string, string>, names: string[]): string | null {
  const keys = Object.keys(row);
  for (const n of names) {
    const hit = keys.find((k) => norm(k) === norm(n)) ?? keys.find((k) => norm(k).includes(norm(n)));
    if (hit !== undefined) return row[hit] ?? null;
  }
  return null;
}

function number(v: string | null): number | null {
  if (v === null) return null;
  const t = v.trim().replace(/,/g, "");
  if (t === "" || !/^-?\d+(\.\d+)?$/.test(t)) return null;
  return Number(t);
}

/** "1", "0,1", "commercial" or "Informational, Transactional" → codes. Null when nothing readable. */
export function parseIntent(v: string | null): number[] | null {
  if (v === null) return null;
  const parts = v.split(/[,;|/]/).map((p) => p.trim().toLowerCase()).filter(Boolean);
  const codes: number[] = [];
  for (const p of parts) {
    if (/^[0-3]$/.test(p)) codes.push(Number(p));
    else {
      const byName = Object.entries(KEYWORD_INTENT_LABEL).find(([, label]) => label.toLowerCase() === p);
      if (byName) codes.push(Number(byName[0]));
    }
  }
  return codes.length ? [...new Set(codes)] : null;
}

export function metricsOf(row: Record<string, string>): KeywordMetrics {
  return {
    volume: number(cell(row, ["Search Volume", "Nq", "Volume"])),
    cpc: number(cell(row, ["CPC", "Cp"])),
    competition: number(cell(row, ["Competition", "Competitive Density", "Co"])),
    results: number(cell(row, ["Number of Results", "Results", "Nr"])),
    difficulty: number(cell(row, ["Keyword Difficulty", "Kd", "Difficulty"])),
    intent: parseIntent(cell(row, ["Intents", "Intent", "In"])),
  };
}

export const keywordOf = (row: Record<string, string>): string => (cell(row, ["Keyword", "Phrase", "Ph"]) ?? "").trim();

export type KeywordList = { market: KeywordMarket; from: "related" | "question"; table: SemrushTable };

/** Merges the lists Semrush returned into one row per keyword. Volume checks (`overview`) fill the market a keyword was missing from. */
export function mergeKeywordLists(lists: KeywordList[], overview: Array<{ market: KeywordMarket; table: SemrushTable }> = []): KeywordRow[] {
  const byKey = new Map<string, KeywordRow>();
  const row = (keyword: string): KeywordRow => {
    const k = keyword.toLowerCase();
    let r = byKey.get(k);
    if (!r) {
      r = { keyword, from: [], us: null, ca: null };
      byKey.set(k, r);
    }
    return r;
  };
  for (const l of lists) {
    for (const t of l.table.rows) {
      const kw = keywordOf(t);
      if (!kw) continue;
      const r = row(kw);
      if (!r.from.includes(l.from)) r.from.push(l.from);
      r[l.market] = r[l.market] ?? metricsOf(t);
    }
  }
  for (const o of overview) {
    for (const t of o.table.rows) {
      const kw = keywordOf(t);
      const existing = kw ? byKey.get(kw.toLowerCase()) : undefined;
      if (existing) existing[o.market] = existing[o.market] ?? metricsOf(t);
    }
  }
  return [...byKey.values()].sort((a, b) => (b.us?.volume ?? -1) - (a.us?.volume ?? -1) || a.keyword.localeCompare(b.keyword));
}

/** Keywords one market has no figures for (to ask Semrush for them), in blocks Semrush accepts. */
export function missingIn(rows: KeywordRow[], market: KeywordMarket): string[][] {
  const missing = rows.filter((r) => r[market] === null).map((r) => r.keyword);
  const blocks: string[][] = [];
  for (let i = 0; i < missing.length; i += KEYWORD_LIMITS.overviewBatch) blocks.push(missing.slice(i, i + KEYWORD_LIMITS.overviewBatch));
  return blocks;
}

/** The team's rule for a new keyword: 30 or more searches a month, US and Canada together. */
export const KEYWORD_VOLUME_FLOOR_COMBINED = 30;
export const combinedVolume = (r: KeywordRow): number | null => (r.us?.volume == null && r.ca?.volume == null ? null : (r.us?.volume ?? 0) + (r.ca?.volume ?? 0));
